'use client';

import { useEffect, useCallback, ReactNode } from 'react';
import styles from './Modal.module.css';
import Button from './Button';

export interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    footer?: ReactNode;
    variant?: 'default' | 'danger';
}

export default function Modal({
    isOpen,
    onClose,
    title,
    children,
    footer,
    variant = 'default',
}: ModalProps) {
    // Close on ESC key
    const handleEsc = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        },
        [onClose]
    );

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleEsc);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEsc);
            document.body.style.overflow = '';
        };
    }, [isOpen, handleEsc]);

    if (!isOpen) return null;

    return (
        <div className={styles.overlay} onClick={onClose}>
            <div
                className={styles.modal}
                onClick={(e) => e.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-title"
            >
                <div className={`${styles.header} ${variant === 'danger' ? styles.headerDanger : ''}`}>
                    <span id="modal-title" className={styles.title}>
                        {title}
                    </span>
                    <button
                        className={styles.closeBtn}
                        onClick={onClose}
                        aria-label="Close modal"
                    >
                        ✕
                    </button>
                </div>
                <div className={styles.body}>{children}</div>
                {footer && <div className={styles.footer}>{footer}</div>}
            </div>
        </div>
    );
}

// Convenience component for confirm dialogs
export interface ConfirmModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title?: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    loading?: boolean;
    variant?: 'default' | 'danger';
}

export function ConfirmModal({
    isOpen,
    onClose,
    onConfirm,
    title = 'confirm_action',
    message,
    confirmText = '[ CONFIRM ]',
    cancelText = '[ CANCEL ]',
    loading = false,
    variant = 'danger',
}: ConfirmModalProps) {
    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={title}
            variant={variant}
            footer={
                <div className={styles.actions}>
                    <Button
                        variant={variant === 'danger' ? 'danger' : 'primary'}
                        onClick={onConfirm}
                        disabled={loading}
                        fullWidth
                    >
                        {loading ? '[ PROCESSING... ]' : confirmText}
                    </Button>
                    <Button variant="secondary" onClick={onClose} fullWidth>
                        {cancelText}
                    </Button>
                </div>
            }
        >
            <p>{message}</p>
        </Modal>
    );
}
